import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

import { GenericUtils } from '@unpispas/upp-defs';

import { adhocService } from './adhoc';
import { stateService } from './state';
import { storeService } from './store';

/**
 * Device fingerprinting and terminal identity for POS clients.
 *
 * Combines FingerprintJS browser fingerprinting with server-side persistence
 * (`device/getuuid`, `device/setuuid` → `POSDEVICE`). Three operational flows:
 *
 * - **Registered terminal:** {@link IsKnown} is true; {@link DeviceId}
 *   resolves the id (local cache → server → calculated fingerprint) and syncs
 *   it to the server. Used at bootstrap (e.g. {@link syncService}) when Access
 *   is not `GUEST`.
 * - **First registration:** {@link TryDevice} builds a provisional id
 *   (fingerprint + terminal name), exposes it via {@link stateService.device}
 *   for login, then {@link UseDevice} commits or rolls back after credentials
 *   succeed or fail. Server rows are only created on commit (`rollback === false`).
 * - **Guest client:** {@link DeviceId} reads {@link stateService.Access} `GUEST` and sets
 *   {@link stateService.device} from the browser fingerprint only — no `device/getuuid` /
 *   `device/setuuid`. Invoked by {@link syncService} at bootstrap.
 */
@Injectable({
    providedIn: 'root'
})
export class identificationService {

    /**
     * @param adhoc - HTTP bridge for `device/getuuid` and `device/setuuid`.
     * @param store - Local persistence for `upp-stored-deviceuuid-v2`.
     * @param state - Runtime device id exposed to login and sync (`state.device`).
     */
    constructor(
        private adhoc: adhocService,
        private store: storeService,
        private state: stateService
    ) {
        // nothing to do
    }

    /**
     * Browser fingerprint **without** terminal name (FingerprintJS + browser signals).
     *
     * Uses {@link https://github.com/fingerprintjs/fingerprintjs | FingerprintJS} when
     * available; on failure falls back to a CRC32 hash of mime types, user agent digits,
     * plugins, and screen metrics prefixed with `BROWSER`.
     *
     * @returns `VISITOR [{visitorId} - BROWSER{hex}]` on success, or `BROWSER{hex}` only
     *          when FingerprintJS fails.
     */
    private async VisitorId(): Promise <string> {
        let _fingerprt = "";
        let _browserid = "";

        _browserid += window.navigator.mimeTypes.length.toString();
        _browserid += window.navigator.userAgent.replace(/\D+/g, '');
        _browserid += window.navigator.plugins.length;
        _browserid += window.screen.height.toString() || '';
        _browserid += window.screen.width.toString() || '';
        _browserid += window.screen.pixelDepth.toString() || '';

        _browserid = "BROWSER" + GenericUtils.CRC32(_browserid).toString(16);

        const _promise = FingerprintJS.load();
        try {
            const _fingerprint = await _promise;
            if (_fingerprint){
                const _result = await _fingerprint.get();
                if (_result){
                    _fingerprt = "VISITOR [" + _result.visitorId + " - " + _browserid + "]";
                }
            }
        }
        catch (error) {
            console.error("[FINGERPRINT] Returned error (falling back to unsecure method)", error );
            _fingerprt = _browserid;
        }

        return _fingerprt;
    }

    /** Local storage key for the persisted terminal uuid (`upp-stored-deviceuuid-v2`). */
    private _storeddevicekey = 'upp-stored-deviceuuid-v2';
    /** Browser localStorage key for the user-provided terminal label (`upp-stored-devicecustom`). */
    private _storeddevicecustom = 'upp-stored-devicecustom';
    /** Adhoc endpoint: resolve uuid by calculated fingerprint (`device/getuuid`). */
    private _doGetDeviceUrl = 'device/getuuid';
    /** Adhoc endpoint: create or update `POSDEVICE` (`device/setuuid`). */
    private _doSetDeviceUrl = 'device/setuuid';

    private _readLocalString(key: string): string | null {
        try {
            if (typeof localStorage === 'undefined'){
                return null;
            }

            return localStorage.getItem(key);
        }
        catch {
            return null;
        }
    }

    private _writeLocalString(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        }
        catch (error) {
            console.error("[DEVICE ID] localStorage set failed for key '" + key + "'", error);
        }
    }

    private _removeLocalString(key: string): void {
        try {
            localStorage.removeItem(key);
        }
        catch {
            // ignore
        }
    }

    /**
     * Reads the persisted terminal uuid, preferring browser localStorage so identity survives
     * IndexedDB clears (Ionic Storage).
     */
    private async _getStoredDeviceId(): Promise <string | null> {
        const _local = this._readLocalString(this._storeddevicekey);
        if (_local){
            return _local;
        }

        return await this.store.Get(this._storeddevicekey);
    }

    /** Persists the terminal uuid in localStorage and Ionic Storage. */
    private async _setStoredDeviceId(value: string): Promise <void> {
        this._writeLocalString(this._storeddevicekey, value);
        await this.store.Set(this._storeddevicekey, value);
    }

    /** Removes the persisted terminal uuid from localStorage and Ionic Storage. */
    private async _delStoredDeviceId(): Promise <void> {
        this._removeLocalString(this._storeddevicekey);
        await this.store.Del(this._storeddevicekey);
    }

    /** Reads the user-provided terminal label, preferring browser localStorage. */
    private async _getStoredDeviceCustom(): Promise <string | null> {
        const _local = this._readLocalString(this._storeddevicecustom);
        if (_local){
            return _local;
        }

        return await this.store.Get(this._storeddevicecustom);
    }

    /** Persists the user-provided terminal label in localStorage and Ionic Storage. */
    private async _setStoredDeviceCustom(value: string): Promise <void> {
        this._writeLocalString(this._storeddevicecustom, value);
        await this.store.Set(this._storeddevicecustom, value);
    }

    /** Removes the user-provided terminal label from localStorage and Ionic Storage. */
    private async _delStoredDeviceCustom(): Promise <void> {
        this._removeLocalString(this._storeddevicecustom);
        await this.store.Del(this._storeddevicecustom);
    }

    /**
     * Resolves the terminal display name for server sync: cached custom label first,
     * then the `(name)` suffix of the operational device id.
     */
    private async _ResolveTerminalName(deviceid: string): Promise <string | null> {
        const _custom = String(await this._getStoredDeviceCustom() || '').trim();
        if (_custom){
            return _custom;
        }

        if (!deviceid || !deviceid.endsWith(')')){
            return null;
        }

        const _open = deviceid.lastIndexOf('(');
        if (_open < 0){
            return null;
        }

        const _parsed = deviceid.slice(_open + 1, -1).trim();
        return _parsed.length > 0 ? _parsed : null;
    }

    /**
     * Whether a terminal id is already persisted locally (successful registration in a prior session).
     *
     * Does not contact the server. A `false` result means the UI should treat the client as a
     * new device (e.g. show terminal name on login) and must not call {@link DeviceId} until
     * after {@link UseDevice} has committed.
     *
     * @returns `true` when {@link _storeddevicekey} has a value in localStorage or {@link storeService}.
     */
    async IsKnown(): Promise <boolean> {
        return !!(await this._getStoredDeviceId());
    }

    /**
     * Resolves the device id.
     *
     * When {@link stateService.Access} is `GUEST`, returns the browser fingerprint,
     * sets {@link stateService.device}, and performs no server round-trip and no
     * local terminal cache write.
     *
     * Otherwise (registered POS terminal): local {@link _storeddevicekey} →
     * server `device/getuuid` → {@link VisitorId}. Always fires `device/setuuid`
     * and writes local storage when the id was not already cached. Assigns
     * {@link stateService.device} with the resolved id.
     *
     * @returns The operational device id (fingerprint, cached uuid, or server uuid).
     */
    async DeviceId() : Promise <string> {
        if (this.state.Access == 'GUEST'){
            const _deviceid = await this.VisitorId();
            this.state.device = _deviceid;
            return _deviceid;
        }

        const _caculated = await this.VisitorId();

        let _stored = false;
        let _deviceid = await this._getStoredDeviceId();
        if (_deviceid){
            _stored = true;
        }

        // uuid not found in store: recover from server (falls to calculated value)
        if (!_deviceid){
            _deviceid = _caculated;

            try {
                const _response = await this.adhoc.DoRequest(this._doGetDeviceUrl, { device: _caculated });
                if (_response['errorcode'] == 0){
                    _deviceid = _response['uuid'];
                }
                else {
                    if (_response['errorcode'] != 3){
                        console.error("[DEVICE ID] GET (uuid) returned errorcode [" + _response['errorcode'] + "]");
                    }
                }
            }
            catch {
                console.error("[DEVICE ID] GET (uuid) request to '" + this._doGetDeviceUrl + "' failed");
            }
        }

        // store the obtained deviceid into the server (related to the calculated value)
        const _resolved = _deviceid || _caculated;
        const _name = await this._ResolveTerminalName(_resolved);
        const _payload: { calc: string; uuid: string; name?: string } = {
            calc: _caculated,
            uuid: _resolved
        };
        if (_name){
            _payload.name = _name;
        }

        this.adhoc.DoRequest(this._doSetDeviceUrl, _payload).then(
        (_response: any) => {
            if (_response['errorcode'] != 0) {
                console.error("[DEVICE ID] SET (uuid) returned errorcode [" + _response['errorcode'] + "]");
            }
        });

        // update device id in storage (if it was not obtained from storage)
        if (!_stored) {
            this._setStoredDeviceId(_resolved).then(
            () => {
                console.info("[DEVICE ID] " + _resolved);
            });
        }

        this.state.device = _resolved;
        return _resolved;
    }

    /** Terminal name from the current {@link TryDevice} session; cleared on commit or rollback. */
    private _tryname: string | null = null;

    /**
     * Starts a provisional terminal identity for first-registration login.
     *
     * Builds `{@link VisitorId}({name})`, persists it under {@link _storeddevicekey}, and sets
     * {@link stateService.device}. Does **not** call the server; {@link UseDevice} must commit or
     * revert after login.
     *
     * @param name - Human-readable terminal name (e.g. cash desk label); must be non-empty and
     *               already trimmed by the caller.
     * @returns The provisional device id used for login and sync until commit.
     * @throws When `name` is empty.
     */
    async TryDevice(name: string): Promise <string> {
        if (!name){
            throw new Error("[DEVICE ID] Device name is required");
        }

        this._tryname = name;

        const _deviceid = await this.VisitorId() + '(' + name + ')';
        await this._setStoredDeviceCustom(name);
        await this._setStoredDeviceId(_deviceid);
        this.state.device = _deviceid;

        return _deviceid;
    }

    /**
     * Clears provisional registration state (cache, {@link stateService.device}, {@link _tryname}).
     * @internal
     */
    private async _revertProvisionalDevice(): Promise <void> {
        this._tryname = null;
        this.state.device = '';
        await this._delStoredDeviceId();
        await this._delStoredDeviceCustom();
    }

    /**
     * Finalises or aborts a {@link TryDevice} first-registration session.
     *
     * @param rollback - When `true`, drops provisional cache and clears {@link stateService.device}
     *                   without touching `POSDEVICE`. When `false`, persists the terminal on the
     *                   server via `device/setuuid` (`calc`, `uuid`, `name`) and keeps local cache.
     * @returns The committed device id when `rollback` is `false`; nothing when rolling back.
     * @throws When committing without a valid {@link _tryname} or provisional id in storage, or
     *         when `device/setuuid` returns a non-zero `errorcode` (state is reverted first).
     */
    async UseDevice(rollback: boolean): Promise <string | void> {
        if (rollback) {
            await this._revertProvisionalDevice();
            return;
        }

        if (!this._tryname){
            await this._revertProvisionalDevice();
            throw new Error("[DEVICE ID] Invalid terminal registration session");
        }

        const _deviceid = await this._getStoredDeviceId();
        if (!_deviceid){
            await this._revertProvisionalDevice();
            throw new Error("[DEVICE ID] No provisional device in cache");
        }

        const _name = this._tryname;
        const _response = await this.adhoc.DoRequest(this._doSetDeviceUrl, {
            calc: _deviceid,
            uuid: _deviceid,
            name: _name
        });

        if (_response['errorcode'] != 0) {
            await this._revertProvisionalDevice();
            throw new Error("[DEVICE ID] SET (uuid) returned errorcode [" + _response['errorcode'] + "]");
        }

        await this._setStoredDeviceCustom(_name);
        await this._setStoredDeviceId(_deviceid);

        this._tryname = null;
        this.state.device = _deviceid;

        return _deviceid;
    }
}
